import { isSupportedLanguage } from "./language.js";

// Saves a signed-in user's language switches to their account. Pure: the
// request, the cache and the UI are passed in, so this runs in Node tests.
//
// Overlapping switches (click en, then bs, before the first save returns) must
// never leave the device on a language the account does not have. So:
//   - saves are sent one at a time, in click order, so the server sees them in
//     the order they were made;
//   - only the newest switch decides the outcome: an older one that fails while
//     a newer one exists does nothing;
//   - when the newest fails, the device goes back to the language the account
//     is confirmed to have (`getAccountLanguage`, which every successful save
//     updates), not to whatever this call happened to see before it.
export function createLanguageSaver({ save, onSaved, apply, getAccountLanguage, onFailed }) {
	let newest = 0;
	let queue = Promise.resolve();

	// `context` is whatever the caller needs back in onSaved, such as which
	// account switched.
	return function saveSwitch(next, context) {
		const mine = ++newest;

		async function run() {
			let saved = false;
			try {
				await save(next);
				saved = true;
			} catch {
				// Handled below, once it is known whether this switch still decides.
			}

			if (saved) {
				onSaved(next, context);
				return;
			}

			if (mine !== newest) return;

			const confirmed = getAccountLanguage();
			if (isSupportedLanguage(confirmed)) apply(confirmed);
			onFailed();
		}

		const outcome = queue.then(run);
		// A throwing callback must not stop the switches queued behind this one.
		queue = outcome.catch(() => {});
		return outcome;
	};
}
